import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { calculateRecordPriceDetails } from '../utils/interestCalculation';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiCheckCircle, 
  FiAlertCircle, 
  FiUploadCloud, 
  FiFileText, 
  FiTrash2, 
  FiPlus, 
  FiEdit2, 
  FiX, 
  FiArrowRight, 
  FiArrowLeft, 
  FiCheck, 
  FiCalendar, 
  FiDollarSign, 
  FiFilter, 
  FiSearch, 
  FiRefreshCw, 
  FiInfo, 
  FiArrowUp, 
  FiArrowDown,
  FiLink,
  FiLayers,
  FiCheckSquare
} from 'react-icons/fi';
import { toast } from 'sonner';
import { formatBRL } from '../utils/format';
import { parseBankStatementFile } from '../utils/bankStatementParser';
import { SelectWithAdd } from '../components/SelectWithAdd';
import { CrudListDialog } from '../components/CrudListDialog';
import { InactiveSubscriptionCard } from '../components/InactiveSubscriptionCard';
import { EssentialPlanRequiredCard } from '../components/EssentialPlanRequiredCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Conciliacao = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ADMIN_EMAILS = ['matheusrweber@gmail.com', 'keylenaiara13@gmail.com'];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);
  const isTrial = Boolean(
    !isAdmin &&
    user?.plan_type !== 'basic' &&
    user?.plan_type !== 'essential' &&
    (user?.trial_period || user?.plan_type === 'trial' || (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date()))
  );
  const isSubscriptionActive = isAdmin || user?.subscription_status === 'active' || isTrial;
  const isBasic = !isAdmin && isSubscriptionActive && user?.plan_type === 'basic';

  // Current step: 1 = Selection & Upload, 2 = Reconciliation Matching
  const [step, setStep] = useState(1);

  // Reference data
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [newBankName, setNewBankName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newPaymentMethodName, setNewPaymentMethodName] = useState('');
  const [banksDialogOpen, setBanksDialogOpen] = useState(false);
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [payMethodDialogOpen, setPayMethodDialogOpen] = useState(false);

  // Step 1: Form filters & File upload
  const now = new Date();
  const currentMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const currentMonthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const [startDate, setStartDate] = useState(currentMonthStart);
  const [endDate, setEndDate] = useState(currentMonthEnd);
  const [selectedBank, setSelectedBank] = useState('');
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null);
  const [parsedTransactions, setParsedTransactions] = useState([]);
  const [parsingLoading, setParsingLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Step 2: System Records & Reconciliation Workspace
  const [systemRecords, setSystemRecords] = useState([]);
  const [statementRecords, setStatementRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  // Manual matching selections (IDs)
  const [selectedSystemId, setSelectedSystemId] = useState(null);
  const [selectedStatementId, setSelectedStatementId] = useState(null);

  // Hidden/Dismissed IDs for current session (Case 3: Remove without deleting)
  const [hiddenSystemIds, setHiddenSystemIds] = useState(new Set());
  const [hiddenStatementIds, setHiddenStatementIds] = useState(new Set());

  // History of reconciled items in current session
  const [reconciledCount, setReconciledCount] = useState(0);
  const [reconciledTotalAmount, setReconciledTotalAmount] = useState(0);

  // Search filter inside Step 2
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilterStep2, setStatusFilterStep2] = useState('all'); // all, auto, pending

  // Quick edit modal for System Record (Case 2: Divergence)
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({
    description: '',
    value: '',
    record_type: 'pagar',
    reference_date: '',
    due_date: '',
    payment_date: '',
    bank_name: '',
    category_name: '',
    payment_method_name: ''
  });
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Full Create modal for Statement Record (Case 1: Missing record in system)
  const EMPTY_CREATE_FORM = {
    description: '',
    installments: 1,
    value: '',
    is_paid: true,
    record_type: 'pagar',
    reference_date: '',
    due_date: '',
    payment_date: '',
    payment_method_name: '',
    bank_name: '',
    category_name: '',
    observation: '',
    enable_interest: false,
    interest_daily_rate: '',
    max_interest_rate: '',
    enable_early_discount: false,
    early_discount_daily_rate: ''
  };

  const [createFromStatementModalOpen, setCreateFromStatementModalOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState(EMPTY_CREATE_FORM);
  const [createModalTab, setCreateModalTab] = useState('geral');
  const [currentCreatingStatementItem, setCurrentCreatingStatementItem] = useState(null);

  // Completion summary dialog
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);

  // Load initial reference data
  useEffect(() => {
    if (isSubscriptionActive) {
      loadBanks();
      loadCategories();
      loadPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriptionActive]);

  const loadBanks = async () => {
    try {
      const res = await axios.get(`${API}/banks`, { withCredentials: true });
      setBanks(res.data);
      if (res.data.length > 0 && !selectedBank) {
        setSelectedBank(res.data[0].name);
      }
    } catch (_e) { /* noop */ }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(res.data);
    } catch (_e) { /* noop */ }
  };

  const loadPaymentMethods = async () => {
    try {
      const res = await axios.get(`${API}/payment-methods`, { withCredentials: true });
      setPaymentMethods(res.data);
    } catch (_e) { /* noop */ }
  };

  const addPaymentMethod = async (payload) => {
    const name = (typeof payload === 'object' ? payload?.name : newPaymentMethodName) || newPaymentMethodName;
    const color = (typeof payload === 'object' ? payload?.color : null) || '#3B82F6';
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API}/payment-methods`, { name: name.trim(), color }, { withCredentials: true });
      setNewPaymentMethodName('');
      loadPaymentMethods();
      toast.success('Forma de pagamento criada!');
    } catch (_e) { toast.error('Erro ao criar forma de pagamento'); }
  };

  const updatePaymentMethod = async (id, updatedItem) => {
    try {
      await axios.put(`${API}/payment-methods/${id}`, updatedItem, { withCredentials: true });
      loadPaymentMethods();
      toast.success('Forma de pagamento atualizada!');
    } catch (_e) { toast.error('Erro ao atualizar forma de pagamento'); }
  };

  const deletePaymentMethod = async (id) => {
    try {
      await axios.delete(`${API}/payment-methods/${id}`, { withCredentials: true });
      loadPaymentMethods();
      toast.success('Forma de pagamento removida');
    } catch (_e) { toast.error('Erro ao remover forma de pagamento'); }
  };

  const addBank = async (payload) => {
    const name = (typeof payload === 'object' ? payload?.name : newBankName) || newBankName;
    const color = (typeof payload === 'object' ? payload?.color : null) || '#1E3A8A';
    const nameToAdd = (name || '').trim();
    if (!nameToAdd) return;
    try {
      await axios.post(`${API}/banks`, { name: nameToAdd, color }, { withCredentials: true });
      setNewBankName('');
      loadBanks();
      toast.success('Banco criado com sucesso!');
    } catch (_e) { toast.error('Erro ao criar banco'); }
  };

  const updateBank = async (id, updatedItem) => {
    try {
      await axios.put(`${API}/banks/${id}`, updatedItem, { withCredentials: true });
      loadBanks();
      toast.success('Banco atualizado com sucesso!');
    } catch (_e) { toast.error('Erro ao atualizar banco'); }
  };

  const deleteBank = async (id) => {
    try {
      await axios.delete(`${API}/banks/${id}`, { withCredentials: true });
      loadBanks();
      toast.success('Banco removido');
    } catch (_e) { toast.error('Erro ao remover banco'); }
  };

  const addCategory = async (payload) => {
    const name = (typeof payload === 'object' ? payload?.name : newCategoryName) || newCategoryName;
    const color = (typeof payload === 'object' ? payload?.color : null) || '#10B981';
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: name.trim(), color }, { withCredentials: true });
      setNewCategoryName('');
      loadCategories();
      toast.success('Categoria criada!');
    } catch (_e) { toast.error('Erro ao criar categoria'); }
  };

  const updateCategory = async (id, updatedItem) => {
    try {
      await axios.put(`${API}/categories/${id}`, updatedItem, { withCredentials: true });
      loadCategories();
      toast.success('Categoria atualizada!');
    } catch (_e) { toast.error('Erro ao atualizar categoria'); }
  };

  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/categories/${id}`, { withCredentials: true });
      loadCategories();
      toast.success('Categoria removida');
    } catch (_e) { toast.error('Erro ao remover categoria'); }
  };

  // Handle file selection and parsing
  const handleFile = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.ofx') && !name.endsWith('.csv') && !name.endsWith('.txt')) {
      toast.error('Por favor selecione um arquivo válido (.ofx ou .csv)');
      return;
    }

    setParsingLoading(true);
    try {
      const result = await parseBankStatementFile(file);
      setUploadedFileInfo({
        fileName: result.fileName,
        fileType: result.fileType,
        fileSize: (result.fileSize / 1024).toFixed(1) + ' KB',
        count: result.transactions.length
      });
      setParsedTransactions(result.transactions);
      toast.success(`Arquivo lido com sucesso! ${result.transactions.length} transações detectadas.`);
    } catch (err) {
      console.error('Error parsing file:', err);
      toast.error(err.message || 'Erro ao processar o arquivo bancário.');
      setUploadedFileInfo(null);
      setParsedTransactions([]);
    } finally {
      setParsingLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleClearFile = () => {
    setUploadedFileInfo(null);
    setParsedTransactions([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Advance from Step 1 to Step 2
  const handleContinueToStep2 = async () => {
    if (!startDate || !endDate) {
      toast.error('Informe a data inicial e final do período.');
      return;
    }
    if (!selectedBank) {
      toast.error('Selecione o banco correspondente ao extrato.');
      return;
    }
    if (!parsedTransactions || parsedTransactions.length === 0) {
      toast.error('Faça o upload de um extrato válido antes de continuar.');
      return;
    }

    setLoadingRecords(true);
    try {
      // Query non-reconciled system records for the bank and period
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        date_type: 'reference',
        bank: selectedBank,
        reconciled_filter: 'unreconciled'
      });

      const res = await axios.get(`${API}/financial-records?${params.toString()}`, { withCredentials: true });
      setSystemRecords(res.data || []);
      setStatementRecords([...parsedTransactions]);
      setHiddenSystemIds(new Set());
      setHiddenStatementIds(new Set());
      setSelectedSystemId(null);
      setSelectedStatementId(null);
      setStep(2);
      toast.info('Extrato e registros carregados para conciliação.');
    } catch (err) {
      console.error('Error loading system records for reconciliation:', err);
      toast.error('Erro ao carregar registros do sistema para o período e banco selecionados.');
    } finally {
      setLoadingRecords(false);
    }
  };

  // ================= MATCHING ALGORITHM =================
  // Rule: Same value (within 0.01), same type (pagar vs receber), date diff <= 2 days
  const autoMatchedPairs = useMemo(() => {
    const activeSystem = systemRecords.filter(r => !hiddenSystemIds.has(r.record_id));
    const activeStatement = statementRecords.filter(s => !hiddenStatementIds.has(s.id));

    const matches = [];
    const matchedSysIds = new Set();
    const matchedStmtIds = new Set();

    const parseDateToTime = (dStr) => {
      if (!dStr) return null;
      try {
        const parts = dStr.split('-');
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])).getTime();
      } catch {
        return null;
      }
    };

    activeStatement.forEach(stmt => {
      if (matchedStmtIds.has(stmt.id)) return;
      const stmtTime = parseDateToTime(stmt.date);
      if (!stmtTime) return;

      // Find candidates in activeSystem
      let bestCandidate = null;
      let minDateDiffDays = Infinity;

      activeSystem.forEach(sys => {
        if (matchedSysIds.has(sys.record_id)) return;

        // Check type match
        if (sys.record_type !== stmt.type) return;

        // Check amount match (exact or within 0.01 tolerance)
        const sysVal = Math.abs(sys.final_value != null ? sys.final_value : sys.value);
        const stmtVal = Math.abs(stmt.value);
        if (Math.abs(sysVal - stmtVal) > 0.01) return;

        // Check date tolerance: compare with reference_date, due_date, or payment_date
        const datesToCheck = [sys.reference_date, sys.due_date, sys.payment_date]
          .map(parseDateToTime)
          .filter(Boolean);

        let minDiff = Infinity;
        datesToCheck.forEach(t => {
          const diffDays = Math.abs(stmtTime - t) / (1000 * 60 * 60 * 24);
          if (diffDays < minDiff) minDiff = diffDays;
        });

        // Tolerance maximum 2 days
        if (minDiff <= 2.05 && minDiff < minDateDiffDays) {
          minDateDiffDays = minDiff;
          bestCandidate = sys;
        }
      });

      if (bestCandidate) {
        matchedSysIds.add(bestCandidate.record_id);
        matchedStmtIds.add(stmt.id);
        matches.push({
          pairId: `auto_${matches.length + 1}`,
          systemRecord: bestCandidate,
          statementRecord: stmt,
          dateDiffDays: minDateDiffDays
        });
      }
    });

    return matches;
  }, [systemRecords, statementRecords, hiddenSystemIds, hiddenStatementIds]);

  // Fast lookup maps for auto-match indicators
  const autoMatchBySysId = useMemo(() => {
    const map = new Map();
    autoMatchedPairs.forEach(p => map.set(p.systemRecord.record_id, p));
    return map;
  }, [autoMatchedPairs]);

  const autoMatchByStmtId = useMemo(() => {
    const map = new Map();
    autoMatchedPairs.forEach(p => map.set(p.statementRecord.id, p));
    return map;
  }, [autoMatchedPairs]);

  // Filtered views based on search and status
  const visibleSystemRecords = useMemo(() => {
    return systemRecords
      .filter(r => !hiddenSystemIds.has(r.record_id))
      .filter(r => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          (r.description || '').toLowerCase().includes(q) ||
          (r.category_name || '').toLowerCase().includes(q) ||
          String(r.value).includes(q)
        );
      })
      .filter(r => {
        if (statusFilterStep2 === 'auto') return autoMatchBySysId.has(r.record_id);
        if (statusFilterStep2 === 'pending') return !autoMatchBySysId.has(r.record_id);
        return true;
      });
  }, [systemRecords, hiddenSystemIds, searchQuery, statusFilterStep2, autoMatchBySysId]);

  const visibleStatementRecords = useMemo(() => {
    return statementRecords
      .filter(s => !hiddenStatementIds.has(s.id))
      .filter(s => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          (s.description || '').toLowerCase().includes(q) ||
          String(s.value).includes(q)
        );
      })
      .filter(s => {
        if (statusFilterStep2 === 'auto') return autoMatchByStmtId.has(s.id);
        if (statusFilterStep2 === 'pending') return !autoMatchByStmtId.has(s.id);
        return true;
      });
  }, [statementRecords, hiddenStatementIds, searchQuery, statusFilterStep2, autoMatchByStmtId]);

  // Totals calculations
  const systemTotals = useMemo(() => {
    let receitas = 0;
    let despesas = 0;
    systemRecords.filter(r => !hiddenSystemIds.has(r.record_id)).forEach(r => {
      const v = Math.abs(r.final_value != null ? r.final_value : r.value);
      if (r.record_type === 'receber') receitas += v;
      else despesas += v;
    });
    return { receitas, despesas, saldo: receitas - despesas };
  }, [systemRecords, hiddenSystemIds]);

  const statementTotals = useMemo(() => {
    let entradas = 0;
    let saidas = 0;
    statementRecords.filter(s => !hiddenStatementIds.has(s.id)).forEach(s => {
      const v = Math.abs(s.value);
      if (s.type === 'receber') entradas += v;
      else saidas += v;
    });
    return { entradas, saidas, saldo: entradas - saidas };
  }, [statementRecords, hiddenStatementIds]);

  // ================= RECONCILIATION ACTIONS =================

  // Global action: Confirm all automatic matches
  const handleReconcileAllAuto = async () => {
    if (autoMatchedPairs.length === 0) {
      toast.info('Não há correspondências automáticas para confirmar.');
      return;
    }

    try {
      const items = autoMatchedPairs.map(pair => ({
        record_id: pair.systemRecord.record_id,
        payment_date: pair.statementRecord.date
      }));

      const res = await axios.post(`${API}/financial-records/reconcile-batch`, { items }, { withCredentials: true });

      const totalAmount = autoMatchedPairs.reduce((acc, p) => acc + p.statementRecord.value, 0);
      setReconciledCount(prev => prev + items.length);
      setReconciledTotalAmount(prev => prev + totalAmount);

      // Remove reconciled from active lists
      const reconciledSysIds = new Set(items.map(i => i.record_id));
      const reconciledStmtIds = new Set(autoMatchedPairs.map(p => p.statementRecord.id));

      setSystemRecords(prev => prev.filter(r => !reconciledSysIds.has(r.record_id)));
      setStatementRecords(prev => prev.filter(s => !reconciledStmtIds.has(s.id)));
      setSelectedSystemId(null);
      setSelectedStatementId(null);

      toast.success(`${res.data.updated_count || items.length} registros conciliados automaticamente com sucesso!`);
    } catch (err) {
      console.error('Error reconciling batch:', err);
      toast.error('Erro ao conciliar registros automáticos.');
    }
  };

  // Manual Match 1x1
  const handleManualReconcile = async () => {
    if (!selectedSystemId || !selectedStatementId) {
      toast.error('Selecione um registro do sistema e uma transação do extrato para conciliar.');
      return;
    }

    const sys = systemRecords.find(r => r.record_id === selectedSystemId);
    const stmt = statementRecords.find(s => s.id === selectedStatementId);

    if (!sys || !stmt) return;

    try {
      const items = [{
        record_id: sys.record_id,
        payment_date: stmt.date
      }];

      await axios.post(`${API}/financial-records/reconcile-batch`, { items }, { withCredentials: true });

      setReconciledCount(prev => prev + 1);
      setReconciledTotalAmount(prev => prev + stmt.value);

      // Remove from lists
      setSystemRecords(prev => prev.filter(r => r.record_id !== sys.record_id));
      setStatementRecords(prev => prev.filter(s => s.id !== stmt.id));
      setSelectedSystemId(null);
      setSelectedStatementId(null);

      toast.success('Par conciliado manualmente com sucesso!');
    } catch (err) {
      console.error('Error manual reconcile:', err);
      toast.error('Erro ao conciliar o par selecionado.');
    }
  };

  // Case 1: Create missing record in system from statement
  const handleOpenCreateFromStatement = (stmt) => {
    setCurrentCreatingStatementItem(stmt);
    setCreateModalTab('geral');
    const isExpense = stmt.type === 'pagar' || (stmt.amount != null ? stmt.amount < 0 : false);
    const absVal = Math.abs(stmt.value != null ? stmt.value : (stmt.amount != null ? stmt.amount : 0));
    const dateVal = stmt.date || new Date().toISOString().split('T')[0];

    setCreateFormData({
      description: stmt.description || stmt.memo || '',
      installments: 1,
      value: absVal > 0 ? absVal.toString() : '',
      is_paid: true,
      record_type: isExpense ? 'pagar' : 'receber',
      reference_date: dateVal,
      due_date: dateVal,
      payment_date: dateVal,
      bank_name: selectedBank || (banks.length > 0 ? banks[0].name : ''),
      category_name: '',
      payment_method_name: '',
      observation: '',
      enable_interest: false,
      interest_daily_rate: '',
      max_interest_rate: '',
      enable_early_discount: false,
      early_discount_daily_rate: ''
    });
    setCreateFromStatementModalOpen(true);
  };

  const handleSaveCreateFromStatement = async (e) => {
    e.preventDefault();
    if (!currentCreatingStatementItem) return;

    try {
      const payload = {
        ...createFormData,
        value: parseFloat(String(createFormData.value).replace(',', '.')),
        installments: parseInt(createFormData.installments) || 1,
        is_paid: Boolean(createFormData.is_paid),
        reconciled: true,
        status: 'reconciled',
        payment_date: createFormData.payment_date || createFormData.reference_date || null,
        due_date: createFormData.due_date || null,
        enable_interest: Boolean(createFormData.enable_interest),
        interest_daily_rate: createFormData.enable_interest ? (parseFloat(String(createFormData.interest_daily_rate).replace(',', '.')) || 0) : 0,
        max_interest_rate: createFormData.enable_interest ? (parseFloat(String(createFormData.max_interest_rate).replace(',', '.')) || 0) : 0,
        enable_early_discount: Boolean(createFormData.enable_early_discount),
        early_discount_daily_rate: createFormData.enable_early_discount ? (parseFloat(String(createFormData.early_discount_daily_rate).replace(',', '.')) || 0) : 0,
        bank_name: createFormData.bank_name || selectedBank,
        category_name: createFormData.category_name || null,
        payment_method_name: createFormData.payment_method_name || null,
        observation: createFormData.observation || null
      };

      await axios.post(`${API}/financial-records`, payload, { withCredentials: true });

      setReconciledCount(prev => prev + 1);
      setReconciledTotalAmount(prev => prev + (payload.value * payload.installments));

      // Remove the statement item since it is now registered & reconciled
      setStatementRecords(prev => prev.filter(s => s.id !== currentCreatingStatementItem.id));
      setCreateFromStatementModalOpen(false);
      setCurrentCreatingStatementItem(null);

      const parcelasMsg = payload.installments > 1 ? ` (${payload.installments} parcelas criadas)` : '';
      toast.success(`Registro cadastrado e conciliado com sucesso${parcelasMsg}!`);
    } catch (err) {
      console.error('Error creating record from statement:', err);
      toast.error(err?.response?.data?.detail || 'Erro ao cadastrar registro.');
    }
  };

  // Case 2: Quick Edit System Record to fix divergence
  const handleOpenEditSystemRecord = (record) => {
    setEditingRecord(record);
    setEditFormData({
      description: record.description || '',
      value: String(record.value || ''),
      record_type: record.record_type || 'pagar',
      reference_date: record.reference_date || '',
      due_date: record.due_date || '',
      payment_date: record.payment_date || '',
      bank_name: record.bank_name || selectedBank,
      category_name: record.category_name || '',
      payment_method_name: record.payment_method_name || ''
    });
    setEditModalOpen(true);
  };

  const handleSaveEditSystemRecord = async (e) => {
    e.preventDefault();
    if (!editingRecord) return;

    try {
      const payload = {
        description: editFormData.description,
        value: parseFloat(String(editFormData.value).replace(',', '.')),
        record_type: editFormData.record_type,
        reference_date: editFormData.reference_date,
        due_date: editFormData.due_date || null,
        payment_date: editFormData.payment_date || null,
        bank_name: editFormData.bank_name || selectedBank,
        category_name: editFormData.category_name || null,
        payment_method_name: editFormData.payment_method_name || null
      };

      const res = await axios.put(`${API}/financial-records/${editingRecord.record_id}`, payload, { withCredentials: true });

      // Update in local state
      setSystemRecords(prev => prev.map(r => r.record_id === editingRecord.record_id ? res.data : r));
      setEditModalOpen(false);
      setEditingRecord(null);

      toast.success('Registro atualizado com sucesso!');
    } catch (err) {
      console.error('Error updating system record:', err);
      toast.error(err?.response?.data?.detail || 'Erro ao atualizar registro.');
    }
  };

  // Case 3: Remove from current reconciliation view without deleting from DB
  const handleHideSystemRecord = (recordId) => {
    setHiddenSystemIds(prev => new Set(prev).add(recordId));
    if (selectedSystemId === recordId) setSelectedSystemId(null);
    toast.info('Registro ocultado da visualização atual.');
  };

  const handleHideStatementRecord = (statementId) => {
    setHiddenStatementIds(prev => new Set(prev).add(statementId));
    if (selectedStatementId === statementId) setSelectedStatementId(null);
    toast.info('Transação do extrato desconsiderada.');
  };

  // Finish session
  const handleFinishReconciliation = () => {
    setCompletionDialogOpen(true);
  };

  const handleConfirmRedirectToRegistros = () => {
    setCompletionDialogOpen(false);
    navigate('/registros');
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors pb-16">
        <Header />
        <main className="transition-all duration-300">
          <InactiveSubscriptionCard moduleName="Conciliação Bancária" />
        </main>
      </div>
    );
  }

  if (isBasic) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors pb-16">
        <Header />
        <main className="transition-all duration-300">
          <EssentialPlanRequiredCard module="conciliacao" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors pb-16">
      <Header />

      <main className="transition-all duration-300">
        <div className={`${step === 1 ? 'max-w-3xl' : 'max-w-7xl'} mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-all duration-300`}>
        
        {/* Top Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-950/60 px-2.5 py-0.5 rounded-full">
                Módulo Bancário
              </span>
              <span className="text-xs text-slate-400">|</span>
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                {step === 1 ? 'Passo 1: Upload e Filtros' : 'Passo 2: Batimento de Transações'}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-blue-900 dark:text-white">Conciliação Bancária</h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
              Importe extratos OFX/CSV e realize o batimento inteligente com seus registros internos.
            </p>
          </div>

          {step === 2 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-2 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <FiArrowLeft size={16} /> Voltar à Seleção
              </Button>
              <Button
                onClick={handleFinishReconciliation}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-md shadow-emerald-600/20 inline-flex items-center gap-2 font-semibold"
              >
                <FiCheckCircle size={18} /> Concluir Conciliação
              </Button>
            </div>
          )}
        </div>

        {/* ========================================================================= */}
        {/* PASSO 1: SELEÇÃO DE PERÍODO, BANCO E UPLOAD DE ARQUIVO                   */}
        {/* ========================================================================= */}
        {step === 1 && (
          <div className="space-y-6 animate-in fade-in-50 duration-300">
            
            {/* Step Card */}
            <div className="bg-white dark:bg-[#0c1829] border border-blue-900/10 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden p-6 sm:p-8">
              
              <div className="flex items-center gap-3 pb-6 border-b border-slate-100 dark:border-white/5">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950/80 text-blue-900 dark:text-cyan-400 flex items-center justify-center font-bold text-xl flex-shrink-0">
                  1
                </div>
                <div>
                  <h2 className="text-xl font-bold text-blue-900 dark:text-white">Configurar Importação</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Defina o período, a conta bancária e carregue o extrato (.ofx ou .csv)</p>
                </div>
              </div>

              <div className="space-y-6 mt-6">
                
                {/* Period selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start-date" className="text-sm font-bold text-blue-900 dark:text-slate-200">
                      Data Inicial <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="mt-1.5 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-white/10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-date" className="text-sm font-bold text-blue-900 dark:text-slate-200">
                      Data Final <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="mt-1.5 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-white/10"
                    />
                  </div>
                </div>

                {/* Bank selection */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-bold text-blue-900 dark:text-slate-200">
                      Conta / Banco do Extrato <span className="text-red-500">*</span>
                    </Label>
                    <button
                      type="button"
                      onClick={() => setBanksDialogOpen(true)}
                      className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                    >
                      + Gerenciar Bancos
                    </button>
                  </div>

                  <Select value={selectedBank} onValueChange={setSelectedBank}>
                    <SelectTrigger className="mt-1.5 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-white/10">
                      <SelectValue placeholder="Selecione o banco" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.length === 0 ? (
                        <SelectItem value="none" disabled>Nenhum banco cadastrado</SelectItem>
                      ) : (
                        banks.map(b => (
                          <SelectItem key={b.bank_id} value={b.name}>
                            {b.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Drag and Drop Zone */}
                <div>
                  <Label className="text-sm font-bold text-blue-900 dark:text-slate-200 mb-1.5 block">
                    Arquivo de Extrato (.ofx ou .csv) <span className="text-red-500">*</span>
                  </Label>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => e.target.files && handleFile(e.target.files[0])}
                    accept=".ofx,.csv,.txt"
                    className="hidden"
                  />

                  {!uploadedFileInfo ? (
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        isDragging
                          ? 'border-cyan-500 bg-cyan-50/50 dark:bg-cyan-950/30'
                          : 'border-slate-300 dark:border-white/15 bg-slate-50/50 dark:bg-slate-900/40 hover:border-blue-900 dark:hover:border-cyan-400 hover:bg-slate-100/50 dark:hover:bg-slate-900/60'
                      }`}
                    >
                      {parsingLoading ? (
                        <div className="py-4">
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500 mx-auto" />
                          <p className="mt-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Lendo e validando extrato...</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="w-14 h-14 mx-auto rounded-full bg-blue-100 dark:bg-blue-950/80 text-blue-900 dark:text-cyan-400 flex items-center justify-center">
                            <FiUploadCloud size={28} />
                          </div>
                          <div>
                            <p className="text-base font-bold text-blue-900 dark:text-white">
                              Clique para escolher ou arraste o arquivo aqui
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                              Formatos suportados: <strong className="text-slate-700 dark:text-slate-300">.OFX</strong> (padrão bancos) e <strong className="text-slate-700 dark:text-slate-300">.CSV</strong>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-50 dark:bg-slate-900/70 border border-emerald-500/30 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-950/80 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                          <FiFileText size={24} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-sm text-slate-900 dark:text-white">{uploadedFileInfo.fileName}</p>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                              {uploadedFileInfo.fileType}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {uploadedFileInfo.fileSize} • <strong>{uploadedFileInfo.count}</strong> transações encontradas
                          </p>
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleClearFile}
                        className="text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 p-2"
                        title="Remover arquivo"
                      >
                        <FiTrash2 size={18} />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Continue button */}
                <div className="pt-4 border-t border-slate-100 dark:border-white/5">
                  <Button
                    onClick={handleContinueToStep2}
                    disabled={!startDate || !endDate || !selectedBank || !uploadedFileInfo || parsedTransactions.length === 0 || loadingRecords}
                    className="w-full h-12 bg-gradient-to-r from-blue-900 via-blue-800 to-cyan-600 hover:from-blue-800 hover:to-cyan-500 text-white font-bold rounded-xl shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2 text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingRecords ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        Carregando registros...
                      </>
                    ) : (
                      <>
                        Continuar para Batimento de Dados <FiArrowRight size={18} />
                      </>
                    )}
                  </Button>
                </div>

              </div>
            </div>

            {/* Quick Tips */}
            <div className="bg-blue-50/70 dark:bg-blue-950/30 border border-blue-900/10 dark:border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
              <FiInfo className="text-blue-900 dark:text-cyan-400 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-xs text-blue-950 dark:text-blue-200 leading-relaxed">
                <p className="font-semibold mb-1">Dica sobre o arquivo OFX/CSV:</p>
                A maioria dos bancos (Nubank, Itaú, Santander, Bradesco, Banco do Brasil, Inter, C6, etc.) disponibiliza o extrato no formato <strong>OFX</strong> com 1 clique no Internet Banking. O arquivo OFX é o mais recomendado por conter dados precisos de data e valor.
              </div>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* PASSO 2: TELA DE CONCILIAÇÃO (BATIMENTO DE DADOS EM 2 COLUNAS)            */}
        {/* ========================================================================= */}
        {step === 2 && (
          <div className="space-y-6 animate-in fade-in-50 duration-300">
            
            {/* Top Toolbar & Summary Ribbon */}
            <div className="bg-white dark:bg-[#0c1829] border border-blue-900/10 dark:border-white/10 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
              
              {/* Badges Info */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/60 border border-blue-900/10 dark:border-blue-500/20 text-xs font-semibold text-blue-900 dark:text-cyan-300 flex items-center gap-1.5">
                  <span className="text-slate-500 dark:text-slate-400">Banco:</span> {selectedBank}
                </div>
                <div className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                  <FiCalendar size={13} /> {formatDateDisplay(startDate)} a {formatDateDisplay(endDate)}
                </div>
                {autoMatchedPairs.length > 0 && (
                  <div className="px-3 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-950/80 border border-emerald-500/30 text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5 animate-pulse">
                    <FiCheckCircle size={14} /> {autoMatchedPairs.length} Correspondência{autoMatchedPairs.length > 1 ? 's' : ''} Automática{autoMatchedPairs.length > 1 ? 's' : ''} (Verde)
                  </div>
                )}
              </div>

              {/* Search & Filter */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 sm:w-64">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                  <Input
                    type="text"
                    placeholder="Buscar descrição ou valor..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9 text-xs bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-white/10"
                  />
                </div>

                <Select value={statusFilterStep2} onValueChange={setStatusFilterStep2}>
                  <SelectTrigger className="h-9 text-xs w-36 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="auto">Apenas Automáticos</SelectItem>
                    <SelectItem value="pending">Apenas Pendentes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

            </div>

            {/* Quick Action Floating Ribbon for Selection */}
            {(selectedSystemId || selectedStatementId || autoMatchedPairs.length > 0) && (
              <div className="bg-gradient-to-r from-blue-900 via-[#0E2A68] to-cyan-900 text-white rounded-xl p-4 shadow-lg border border-cyan-500/30 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 text-cyan-300">
                    <FiLayers size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-white">Ações de Batimento</h3>
                    <p className="text-xs text-slate-300">
                      {selectedSystemId && selectedStatementId ? (
                        <span className="text-cyan-300 font-semibold">1 item do Sistema e 1 do Extrato selecionados para Match Manual!</span>
                      ) : autoMatchedPairs.length > 0 ? (
                        <span>{autoMatchedPairs.length} pares prontos para conciliação em lote.</span>
                      ) : (
                        <span>Selecione 1 item da esquerda e 1 da direita para vincular manualmente.</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
                  {selectedSystemId && selectedStatementId && (
                    <Button
                      onClick={handleManualReconcile}
                      className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs h-9 px-4 shadow-md flex items-center gap-1.5"
                    >
                      <FiLink size={14} /> Conciliar Selecionados (1x1)
                    </Button>
                  )}

                  {autoMatchedPairs.length > 0 && (
                    <Button
                      onClick={handleReconcileAllAuto}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs h-9 px-4 shadow-md flex items-center gap-1.5"
                    >
                      <FiCheck size={16} /> Conciliar Automáticos ({autoMatchedPairs.length})
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* TWO COLUMNS WORKSPACE */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* ========================================================================= */}
              {/* LADO ESQUERDO: REGISTROS DO SISTEMA                                       */}
              {/* ========================================================================= */}
              <div className="bg-white dark:bg-[#0c1829] border border-blue-900/10 dark:border-white/10 rounded-2xl shadow-sm flex flex-col h-[650px] overflow-hidden">
                
                {/* Column Header */}
                <div className="p-4 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-slate-900/40 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3 rounded-full bg-blue-600" />
                    <div>
                      <h3 className="font-black text-base text-blue-900 dark:text-white leading-none">Registros do Sistema</h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Não conciliados ({visibleSystemRecords.length})</p>
                    </div>
                  </div>

                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-950/60 text-blue-900 dark:text-cyan-300">
                    {selectedBank}
                  </span>
                </div>

                {/* Column Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                  {visibleSystemRecords.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-400">
                      <FiCheckCircle size={36} className="mb-2 text-slate-300 dark:text-slate-600" />
                      <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Nenhum registro pendente</p>
                      <p className="text-xs text-slate-400 mt-1">Todos os registros deste período e banco foram conciliados ou ocultados.</p>
                    </div>
                  ) : (
                    visibleSystemRecords.map((record) => {
                      const autoMatch = autoMatchBySysId.get(record.record_id);
                      const isSelected = selectedSystemId === record.record_id;
                      const val = Math.abs(record.final_value != null ? record.final_value : record.value);

                      return (
                        <div
                          key={record.record_id}
                          onClick={() => setSelectedSystemId(isSelected ? null : record.record_id)}
                          className={`group relative p-3.5 rounded-xl border transition-all cursor-pointer select-none ${
                            isSelected
                              ? 'border-cyan-500 bg-cyan-50/70 dark:bg-cyan-950/40 ring-2 ring-cyan-400/50 shadow-md'
                              : autoMatch
                              ? 'border-emerald-500/80 bg-emerald-50/40 dark:bg-emerald-950/20 hover:border-emerald-500'
                              : 'border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 hover:border-blue-900/40 dark:hover:border-white/20'
                          }`}
                        >
                          {/* Auto Match Tag */}
                          {autoMatch && (
                            <div className="absolute top-2.5 right-2.5 flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-500/30">
                              <FiCheck size={11} /> Auto Match ({autoMatch.pairId})
                            </div>
                          )}

                          <div className="flex items-start justify-between gap-2 pr-20">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                  record.record_type === 'pagar'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300'
                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-cyan-300'
                                }`}>
                                  {record.record_type === 'pagar' ? 'Pagar' : 'Receber'}
                                </span>
                                <h4 className="font-bold text-sm text-slate-900 dark:text-white leading-tight">
                                  {record.description}
                                </h4>
                              </div>

                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                                <span>Ref: <strong>{formatDateDisplay(record.reference_date)}</strong></span>
                                {record.due_date && record.due_date !== record.reference_date && (
                                  <span>Venc: <strong>{formatDateDisplay(record.due_date)}</strong></span>
                                )}
                                {record.category_name && (
                                  <span className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 rounded text-slate-600 dark:text-slate-300">
                                    {record.category_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-white/5 flex items-center justify-between">
                            <div className={`font-extrabold text-sm ${record.record_type === 'pagar' ? 'text-red-600 dark:text-red-400' : 'text-blue-900 dark:text-cyan-400'}`}>
                              {record.record_type === 'pagar' ? '-' : '+'}{formatBRL(val)}
                            </div>

                            {/* Actions (Pencil for quick edit, X for hide) */}
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => handleOpenEditSystemRecord(record)}
                                title="Editar divergência de valor/data"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/50 transition-colors"
                              >
                                <FiEdit2 size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleHideSystemRecord(record.record_id)}
                                title="Remover da conciliação (não apaga do banco)"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
                              >
                                <FiX size={15} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Left Column Footer (Totals) */}
                <div className="p-3.5 border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-900/80 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-bold">Totais Sistema</span>
                    <div className="flex items-center gap-3 font-semibold mt-0.5">
                      <span className="text-blue-700 dark:text-cyan-400">Rec: {formatBRL(systemTotals.receitas)}</span>
                      <span className="text-red-600 dark:text-red-400">Desp: {formatBRL(systemTotals.despesas)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-bold">Saldo</span>
                    <span className={`font-black text-sm ${systemTotals.saldo >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatBRL(systemTotals.saldo)}
                    </span>
                  </div>
                </div>

              </div>

              {/* ========================================================================= */}
              {/* LADO DIREITO: TRANSAÇÕES DO EXTRATO                                       */}
              {/* ========================================================================= */}
              <div className="bg-white dark:bg-[#0c1829] border border-blue-900/10 dark:border-white/10 rounded-2xl shadow-sm flex flex-col h-[650px] overflow-hidden">
                
                {/* Column Header */}
                <div className="p-4 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-slate-900/40 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3 rounded-full bg-cyan-500" />
                    <div>
                      <h3 className="font-black text-base text-blue-900 dark:text-white leading-none">Transações do Extrato</h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {uploadedFileInfo?.fileName || 'Extrato'} ({visibleStatementRecords.length})
                      </p>
                    </div>
                  </div>

                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-cyan-100 dark:bg-cyan-950/60 text-cyan-900 dark:text-cyan-300">
                    {uploadedFileInfo?.fileType || 'OFX/CSV'}
                  </span>
                </div>

                {/* Column Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                  {visibleStatementRecords.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-400">
                      <FiCheckCircle size={36} className="mb-2 text-slate-300 dark:text-slate-600" />
                      <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Extrato concilidado!</p>
                      <p className="text-xs text-slate-400 mt-1">Todas as linhas do extrato foram conciliadas ou desconsideradas.</p>
                    </div>
                  ) : (
                    visibleStatementRecords.map((stmt) => {
                      const autoMatch = autoMatchByStmtId.get(stmt.id);
                      const isSelected = selectedStatementId === stmt.id;
                      const isCredit = stmt.amount >= 0;

                      return (
                        <div
                          key={stmt.id}
                          onClick={() => setSelectedStatementId(isSelected ? null : stmt.id)}
                          className={`group relative p-3.5 rounded-xl border transition-all cursor-pointer select-none ${
                            isSelected
                              ? 'border-cyan-500 bg-cyan-50/70 dark:bg-cyan-950/40 ring-2 ring-cyan-400/50 shadow-md'
                              : autoMatch
                              ? 'border-emerald-500/80 bg-emerald-50/40 dark:bg-emerald-950/20 hover:border-emerald-500'
                              : 'border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 hover:border-blue-900/40 dark:hover:border-white/20'
                          }`}
                        >
                          {/* Auto Match Tag */}
                          {autoMatch && (
                            <div className="absolute top-2.5 right-2.5 flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-500/30">
                              <FiCheck size={11} /> Auto Match ({autoMatch.pairId})
                            </div>
                          )}

                          <div className="flex items-start justify-between gap-2 pr-20">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                  !isCredit
                                    ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300'
                                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                                }`}>
                                  {!isCredit ? 'Saída' : 'Entrada'}
                                </span>
                                <h4 className="font-bold text-sm text-slate-900 dark:text-white leading-tight">
                                  {stmt.description}
                                </h4>
                              </div>

                              <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                                <span>Data: <strong>{formatDateDisplay(stmt.date)}</strong></span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-white/5 flex items-center justify-between">
                            <div className={`font-extrabold text-sm ${!isCredit ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                              {!isCredit ? '-' : '+'}{formatBRL(stmt.value)}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              {!autoMatch && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenCreateFromStatement(stmt)}
                                  className="h-7 text-[11px] font-bold px-2 border-cyan-600/30 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-950/50 flex items-center gap-1"
                                >
                                  <FiPlus size={13} /> Cadastrar
                                </Button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleHideStatementRecord(stmt.id)}
                                title="Desconsiderar transação do extrato"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
                              >
                                <FiX size={15} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Right Column Footer (Totals) */}
                <div className="p-3.5 border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-900/80 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-bold">Totais Extrato</span>
                    <div className="flex items-center gap-3 font-semibold mt-0.5">
                      <span className="text-emerald-600 dark:text-emerald-400">Entr: {formatBRL(statementTotals.entradas)}</span>
                      <span className="text-red-600 dark:text-red-400">Saídas: {formatBRL(statementTotals.saidas)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-bold">Saldo Extrato</span>
                    <span className={`font-black text-sm ${statementTotals.saldo >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatBRL(statementTotals.saldo)}
                    </span>
                  </div>
                </div>

              </div>

            </div>

            {/* Bottom Final Action Bar */}
            <div className="bg-white dark:bg-[#0c1829] border border-blue-900/10 dark:border-white/10 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-slate-500 dark:text-slate-400 text-center sm:text-left">
                Conciliados nesta sessão: <strong className="text-emerald-600 dark:text-emerald-400">{reconciledCount}</strong> registros ({formatBRL(reconciledTotalAmount)})
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
                >
                  <FiArrowLeft size={16} /> Voltar ao Passo 1
                </Button>
                <Button
                  onClick={handleFinishReconciliation}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold px-6 shadow-md shadow-emerald-600/20 flex items-center gap-2"
                >
                  <FiCheckCircle size={18} /> Concluir e Ver Registros
                </Button>
              </div>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* MODAIS & DIALOGS                                                          */}
        {/* ========================================================================= */}

        {/* Modal: Cadastrar no Sistema a partir do Extrato (Caso 1 - Modal Completo) */}
        <Dialog open={createFromStatementModalOpen} onOpenChange={setCreateFromStatementModalOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-white flex items-center gap-2">
                <span>Novo Registro</span>
                {isBasic && createModalTab === 'juros' && (
                  <span className="text-[10px] font-bold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 shadow-xs inline-flex items-center gap-1 leading-none tracking-tight">
                    ⭐ Essencial
                  </span>
                )}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
                Preencha os dados do registro nas abas Geral, Outros e Juros.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSaveCreateFromStatement} className="space-y-4 mt-4">
              <Tabs value={createModalTab} onValueChange={setCreateModalTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger data-testid="tab-stmt-geral" value="geral">Geral</TabsTrigger>
                  <TabsTrigger data-testid="tab-stmt-outros" value="outros">Outros</TabsTrigger>
                  <TabsTrigger data-testid="tab-stmt-juros" value="juros" className="flex items-center justify-center gap-1">
                    <span>Juros</span>
                    {isBasic && (
                      <span className="text-[9px] font-extrabold uppercase px-1 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 leading-none">
                        ⭐
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* GERAL TAB */}
                <TabsContent value="geral" className="space-y-4 mt-4">
                  <div>
                    <Label className="text-blue-900 dark:text-slate-200 font-semibold">Tipo</Label>
                    <RadioGroup
                      value={createFormData.record_type}
                      onValueChange={(v) => setCreateFormData({ ...createFormData, record_type: v })}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="pagar" id="stmt-pagar" />
                        <Label htmlFor="stmt-pagar" className="cursor-pointer text-red-600 font-semibold">Conta a Pagar</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="receber" id="stmt-receber" />
                        <Label htmlFor="stmt-receber" className="cursor-pointer text-blue-900 dark:text-cyan-400 font-semibold">Conta a Receber</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label htmlFor="stmt-description" className="text-blue-900 dark:text-slate-200 font-semibold">Descrição*</Label>
                    <Input
                      id="stmt-description"
                      data-testid="stmt-record-description-input"
                      value={createFormData.description}
                      onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="Ex: Compra supermercado"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="stmt-installments" className="text-blue-900 dark:text-slate-200 font-semibold">Parcelas*</Label>
                      <Input
                        id="stmt-installments"
                        data-testid="stmt-record-installments-input"
                        type="number"
                        min="1"
                        value={createFormData.installments}
                        onChange={(e) => setCreateFormData({ ...createFormData, installments: parseInt(e.target.value) || 1 })}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="stmt-value" className="text-blue-900 dark:text-slate-200 font-semibold">Valor da Parcela (R$)*</Label>
                      <Input
                        id="stmt-value"
                        data-testid="stmt-record-value-input"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={createFormData.value}
                        onChange={(e) => setCreateFormData({ ...createFormData, value: e.target.value })}
                        required
                        className="mt-1"
                        placeholder="0,00"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="stmt-reference-date" className="text-blue-900 dark:text-slate-200 font-semibold">Data de Referência*</Label>
                      <Popover>
                        <PopoverTrigger><FiInfo className="text-slate-500 hover:text-blue-900 dark:hover:text-cyan-400 cursor-help" size={16} /></PopoverTrigger>
                        <PopoverContent className="text-sm">
                          <p className="font-semibold text-blue-900 mb-1">O que é Data de Referência?</p>
                          <p>É a data para quando você <strong>deseja pagar</strong> ou <strong>receber</strong>. Útil para planejar e filtrar por período.</p>
                          <p className="mt-2 text-xs text-slate-600">Se parcelado, as próximas parcelas serão nos próximos meses.</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <Input
                      id="stmt-reference-date"
                      data-testid="stmt-record-reference-date-input"
                      type="date"
                      value={createFormData.reference_date}
                      onChange={(e) => setCreateFormData({ ...createFormData, reference_date: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="stmt-due-date" className="text-blue-900 dark:text-slate-200 font-semibold">Data de Vencimento</Label>
                      <Popover>
                        <PopoverTrigger><FiInfo className="text-slate-500 hover:text-blue-900 dark:hover:text-cyan-400 cursor-help" size={16} /></PopoverTrigger>
                        <PopoverContent className="text-sm">
                          <p className="font-semibold text-blue-900 mb-1">Aviso de Vencimento</p>
                          <p>Não será enviada notificação de aviso de vencimento se este campo estiver em branco.</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <Input
                      id="stmt-due-date"
                      data-testid="stmt-record-due-date-input"
                      type="date"
                      value={createFormData.due_date || ''}
                      onChange={(e) => setCreateFormData({ ...createFormData, due_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="stmt-is-paid"
                      data-testid="stmt-record-paid-checkbox"
                      checked={createFormData.is_paid}
                      onCheckedChange={(c) => setCreateFormData({ ...createFormData, is_paid: c })}
                    />
                    <Label htmlFor="stmt-is-paid" className="text-blue-900 dark:text-slate-200 font-semibold cursor-pointer">Pago</Label>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-900/60 rounded-lg p-4 border border-slate-200 dark:border-white/10">
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Total:</p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-white">
                      {formatBRL(parseFloat(createFormData.value || 0) * (createFormData.installments || 1))}
                    </p>
                    {createFormData.installments > 1 && (
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        {createFormData.installments} parcelas de {formatBRL(parseFloat(createFormData.value || 0))}
                      </p>
                    )}
                  </div>
                </TabsContent>

                {/* OUTROS TAB */}
                <TabsContent value="outros" className="space-y-4 mt-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="stmt-payment-date" className="text-blue-900 dark:text-slate-200 font-semibold">Data de Pagamento</Label>
                      <Popover>
                        <PopoverTrigger><FiInfo className="text-slate-500 hover:text-blue-900 dark:hover:text-cyan-400 cursor-help" size={16} /></PopoverTrigger>
                        <PopoverContent className="text-sm">
                          <p className="font-semibold text-blue-900 mb-1">O que é Data de Pagamento?</p>
                          <p>É a data em que o pagamento foi <strong>efetivamente realizado</strong>. Preenchida automaticamente a partir do extrato.</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <Input
                      id="stmt-payment-date"
                      data-testid="stmt-record-payment-date-input"
                      type="date"
                      value={createFormData.payment_date}
                      onChange={(e) => setCreateFormData({ ...createFormData, payment_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>

                  <SelectWithAdd
                    label="Forma de Pagamento"
                    value={createFormData.payment_method_name}
                    onChange={(v) => setCreateFormData({ ...createFormData, payment_method_name: v })}
                    options={paymentMethods.map(p => ({ id: p.method_id, name: p.name, color: p.color || '#3B82F6' }))}
                    onManage={() => setPayMethodDialogOpen(true)}
                    testIdSelect="stmt-record-payment-method"
                    testIdManage="stmt-manage-payment-method-button"
                  />

                  <SelectWithAdd
                    label="Banco"
                    value={createFormData.bank_name}
                    onChange={(v) => setCreateFormData({ ...createFormData, bank_name: v })}
                    options={banks.map(b => ({ id: b.bank_id, name: b.name, color: b.color || '#1E3A8A' }))}
                    onManage={() => setBanksDialogOpen(true)}
                    testIdSelect="stmt-record-bank"
                    testIdManage="stmt-manage-bank-button"
                  />

                  <SelectWithAdd
                    label="Categoria"
                    value={createFormData.category_name}
                    onChange={(v) => setCreateFormData({ ...createFormData, category_name: v })}
                    options={categories.map(c => ({ id: c.category_id, name: c.name, color: c.color || '#10B981' }))}
                    onManage={() => setCategoriesDialogOpen(true)}
                    testIdSelect="stmt-record-category"
                    testIdManage="stmt-manage-category-button"
                  />

                  <div>
                    <Label htmlFor="stmt-observation" className="text-blue-900 dark:text-slate-200 font-semibold">Observação</Label>
                    <Textarea
                      id="stmt-observation"
                      data-testid="stmt-record-observation"
                      value={createFormData.observation}
                      maxLength={200}
                      onChange={(e) => setCreateFormData({ ...createFormData, observation: e.target.value })}
                      placeholder="Notas adicionais (máx 200 caracteres)"
                      className="mt-1"
                      rows={3}
                    />
                    <p className="text-xs text-slate-500 mt-1 text-right">{(createFormData.observation || '').length}/200</p>
                  </div>
                </TabsContent>

                {/* JUROS TAB */}
                <TabsContent value="juros" className="space-y-5 mt-4">
                  {isBasic ? (
                    <div className="p-6 bg-amber-50 dark:bg-amber-950/30 border border-amber-500/30 rounded-xl text-center space-y-3 my-4">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto font-bold">
                        %
                      </div>
                      <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 max-w-sm mx-auto">
                        Faça o upgrade para a Assinatura Essencial para usar os registros calculados com juros.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => navigate('/assinatura')}
                        className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-sm"
                      >
                        Fazer Upgrade para o Essencial
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Seção: Cálculo de Juros */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b dark:border-white/10 pb-2">
                      <h4 className="text-base font-bold text-blue-900 dark:text-white">Cálculo de Juros</h4>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="stmt_enable_interest"
                          data-testid="stmt-toggle-enable-interest"
                          checked={Boolean(createFormData.enable_interest)}
                          onCheckedChange={(checked) => setCreateFormData(prev => ({
                            ...prev,
                            enable_interest: checked,
                            interest_daily_rate: checked && (!prev.interest_daily_rate || prev.interest_daily_rate === '0') ? '0.033' : prev.interest_daily_rate,
                            max_interest_rate: checked && (!prev.max_interest_rate || prev.max_interest_rate === '0') ? '2' : prev.max_interest_rate
                          }))}
                        />
                        <Label htmlFor="stmt_enable_interest" className="text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer">
                          Ativar/desativar cálculo de juros
                        </Label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Label htmlFor="stmt_interest_daily_rate" className="text-blue-900 dark:text-slate-200 font-semibold text-sm">
                            Juros ao dia %
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="inline-flex text-slate-400 hover:text-blue-900 dark:hover:text-cyan-400 transition-colors cursor-help" title="A lei só permite cobrar no máximo 1% ao mês, ou seja, 0,033% ao dia.">
                                <FiInfo size={15} />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="text-xs max-w-[260px] p-3 shadow-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10">
                              <p className="font-semibold text-blue-900 dark:text-white mb-1">Juros ao dia %</p>
                              <p className="text-slate-600 dark:text-slate-300">A lei só permite cobrar no máximo 1% ao mês, ou seja, 0,033% ao dia.</p>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <Input
                          id="stmt_interest_daily_rate"
                          data-testid="stmt-record-interest-daily-rate"
                          type="number"
                          step="0.001"
                          min="0"
                          disabled={!createFormData.enable_interest}
                          value={createFormData.interest_daily_rate}
                          onChange={(e) => setCreateFormData({ ...createFormData, interest_daily_rate: e.target.value })}
                          placeholder="0,033"
                          className="mt-1 disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800"
                        />
                      </div>

                      <div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Label htmlFor="stmt_max_interest_rate" className="text-blue-900 dark:text-slate-200 font-semibold text-sm">
                            Calcula juros até quanto?
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="inline-flex text-slate-400 hover:text-blue-900 dark:hover:text-cyan-400 transition-colors cursor-help" title="A lei limita a multa por atraso a no máximo 2% do valor do débito.">
                                <FiInfo size={15} />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="text-xs max-w-[260px] p-3 shadow-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10">
                              <p className="font-semibold text-blue-900 dark:text-white mb-1">Limite de Multa/Juros</p>
                              <p className="text-slate-600 dark:text-slate-300">A lei limita a multa por atraso a no máximo 2% do valor do débito.</p>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <Input
                          id="stmt_max_interest_rate"
                          data-testid="stmt-record-max-interest-rate"
                          type="number"
                          step="0.01"
                          min="0"
                          disabled={!createFormData.enable_interest}
                          value={createFormData.max_interest_rate}
                          onChange={(e) => setCreateFormData({ ...createFormData, max_interest_rate: e.target.value })}
                          placeholder="2"
                          className="mt-1 disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Linha divisória de separação */}
                  <div className="border-t border-slate-200 dark:border-white/10 my-4" />

                  {/* Seção: Pagamento Antecipado */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b dark:border-white/10 pb-2">
                      <h4 className="text-base font-bold text-blue-900 dark:text-white">Pagamento Antecipado</h4>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="stmt_enable_early_discount"
                          data-testid="stmt-toggle-enable-early-discount"
                          checked={Boolean(createFormData.enable_early_discount)}
                          onCheckedChange={(checked) => setCreateFormData(prev => ({
                            ...prev,
                            enable_early_discount: checked,
                            early_discount_daily_rate: checked && (!prev.early_discount_daily_rate || prev.early_discount_daily_rate === '0') ? '0.033' : prev.early_discount_daily_rate
                          }))}
                        />
                        <Label htmlFor="stmt_enable_early_discount" className="text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer">
                          Ativar cálculo de pagamento antecipado
                        </Label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="stmt_early_discount_daily_rate" className="text-blue-900 dark:text-slate-200 font-semibold text-sm">
                          Desconto por dia %
                        </Label>
                        <Input
                          id="stmt_early_discount_daily_rate"
                          data-testid="stmt-record-early-discount-rate"
                          type="number"
                          step="0.001"
                          min="0"
                          disabled={!createFormData.enable_early_discount}
                          value={createFormData.early_discount_daily_rate}
                          onChange={(e) => setCreateFormData({ ...createFormData, early_discount_daily_rate: e.target.value })}
                          placeholder="0,033"
                          className="mt-1 disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Simulação em tempo real */}
                  {(createFormData.enable_interest || createFormData.enable_early_discount) && (
                    <div className="bg-slate-50 dark:bg-slate-900/60 rounded-lg p-4 space-y-1.5 mt-2 border border-slate-200 dark:border-white/10">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">
                          Simulação de Preço Final
                        </span>
                        <span className="text-2xl font-bold text-blue-900 dark:text-white">
                          {formatBRL(calculateRecordPriceDetails({
                            value: createFormData.value,
                            due_date: createFormData.due_date,
                            payment_date: createFormData.payment_date,
                            is_paid: createFormData.is_paid,
                            enable_interest: createFormData.enable_interest,
                            interest_daily_rate: createFormData.interest_daily_rate,
                            max_interest_rate: createFormData.max_interest_rate,
                            enable_early_discount: createFormData.enable_early_discount,
                            early_discount_daily_rate: createFormData.early_discount_daily_rate,
                            record_type: createFormData.record_type
                          }).finalValue)}
                        </span>
                      </div>
                    </div>
                  )}
                    </>
                  )}
                </TabsContent>
              </Tabs>

              <div className="flex gap-3 pt-4 border-t dark:border-white/10">
                <Button type="button" variant="outline" onClick={() => setCreateFromStatementModalOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-blue-900 hover:bg-blue-800 text-white font-bold">
                  Salvar e Conciliar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal: Edição Rápida de Divergência (Caso 2) */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-white">
                Editar Registro do Sistema
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
                Ajuste os campos para alinhar com o extrato bancário.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSaveEditSystemRecord} className="space-y-4 mt-3">
              <div>
                <Label className="text-sm font-bold text-blue-900 dark:text-slate-200">Tipo</Label>
                <RadioGroup
                  value={editFormData.record_type}
                  onValueChange={(v) => setEditFormData({ ...editFormData, record_type: v })}
                  className="flex gap-4 mt-1.5"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="pagar" id="edit-pagar" />
                    <Label htmlFor="edit-pagar" className="cursor-pointer text-red-600 font-semibold text-sm">Conta a Pagar</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="receber" id="edit-receber" />
                    <Label htmlFor="edit-receber" className="cursor-pointer text-blue-900 dark:text-cyan-400 font-semibold text-sm">Conta a Receber</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="edit-desc" className="text-sm font-bold text-blue-900 dark:text-slate-200">Descrição*</Label>
                <Input
                  id="edit-desc"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-val" className="text-sm font-bold text-blue-900 dark:text-slate-200">Valor (R$)*</Label>
                  <Input
                    id="edit-val"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editFormData.value}
                    onChange={(e) => setEditFormData({ ...editFormData, value: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-ref-date" className="text-sm font-bold text-blue-900 dark:text-slate-200">Data de Referência*</Label>
                  <Input
                    id="edit-ref-date"
                    type="date"
                    value={editFormData.reference_date}
                    onChange={(e) => setEditFormData({ ...editFormData, reference_date: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="edit-due-date" className="text-sm font-bold text-blue-900 dark:text-slate-200">Data de Vencimento</Label>
                    <Popover>
                      <PopoverTrigger><FiInfo className="text-slate-500 hover:text-blue-900 dark:hover:text-cyan-400 cursor-help" size={15} /></PopoverTrigger>
                      <PopoverContent className="text-sm">
                        <p className="font-semibold text-blue-900 mb-1">Aviso de Vencimento</p>
                        <p>Não será enviada notificação de aviso de vencimento se este campo estiver em branco.</p>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Input
                    id="edit-due-date"
                    type="date"
                    value={editFormData.due_date || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, due_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-pay-date" className="text-sm font-bold text-blue-900 dark:text-slate-200">Data de Pagamento</Label>
                  <Input
                    id="edit-pay-date"
                    type="date"
                    value={editFormData.payment_date}
                    onChange={(e) => setEditFormData({ ...editFormData, payment_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold text-blue-900 dark:text-slate-200">Banco</Label>
                  <Select
                    value={editFormData.bank_name}
                    onValueChange={(v) => setEditFormData({ ...editFormData, bank_name: v })}
                  >
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione o banco" /></SelectTrigger>
                    <SelectContent>
                      {banks.map(b => (
                        <SelectItem key={b.bank_id} value={b.name}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-bold text-blue-900 dark:text-slate-200">Categoria</Label>
                  <Select
                    value={editFormData.category_name}
                    onValueChange={(v) => setEditFormData({ ...editFormData, category_name: v })}
                  >
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (
                        <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-blue-900 hover:bg-blue-800 text-white font-bold">
                  Salvar Alterações
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal de Conclusão da Sessão */}
        <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
          <DialogContent className="sm:max-w-md text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-2">
              <FiCheckCircle size={36} />
            </div>

            <DialogHeader>
              <DialogTitle className="text-2xl font-black text-blue-900 dark:text-white text-center">
                Conciliação Concluída!
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-600 dark:text-slate-300 text-center">
                Sua sessão de conciliação foi finalizada com sucesso.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-slate-50 dark:bg-slate-900/60 rounded-xl p-4 my-3 text-left space-y-2 border border-slate-200 dark:border-white/10">
              <div className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">Banco Conciliado:</span>
                <span className="font-bold text-slate-800 dark:text-white">{selectedBank}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">Período:</span>
                <span className="font-bold text-slate-800 dark:text-white">{formatDateDisplay(startDate)} a {formatDateDisplay(endDate)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">Registros Conciliados:</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400">{reconciledCount} registros</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500 dark:text-slate-400">Total Movimentado:</span>
                <span className="font-bold text-slate-800 dark:text-white">{formatBRL(reconciledTotalAmount)}</span>
              </div>
            </div>

            <Button
              onClick={handleConfirmRedirectToRegistros}
              className="w-full bg-blue-900 hover:bg-blue-800 text-white font-bold h-11 rounded-xl"
            >
              Ir para Registros Financeiros
            </Button>
          </DialogContent>
        </Dialog>

        {/* Gerenciar Bancos & Categorias Dialogs */}
        <CrudListDialog
          open={banksDialogOpen}
          onOpenChange={setBanksDialogOpen}
          title="Bancos"
          placeholder="Nome do banco"
          newValue={newBankName}
          onNewValueChange={setNewBankName}
          onAdd={addBank}
          items={banks.map(b => ({ id: b.bank_id, name: b.name, color: b.color || '#1E3A8A' }))}
          onUpdate={updateBank}
          onDelete={deleteBank}
          defaultColor="#1E3A8A"
          testIdPrefix="bank"
        />

        <CrudListDialog
          open={categoriesDialogOpen}
          onOpenChange={setCategoriesDialogOpen}
          title="Categorias"
          placeholder="Nome da categoria"
          newValue={newCategoryName}
          onNewValueChange={setNewCategoryName}
          onAdd={addCategory}
          items={categories.map(c => ({ id: c.category_id, name: c.name, color: c.color || '#10B981' }))}
          onUpdate={updateCategory}
          onDelete={deleteCategory}
          defaultColor="#10B981"
          testIdPrefix="category"
        />

        <CrudListDialog
          open={payMethodDialogOpen}
          onOpenChange={setPayMethodDialogOpen}
          title="Formas de Pagamento"
          placeholder="Nome da forma de pagamento"
          newValue={newPaymentMethodName}
          onNewValueChange={setNewPaymentMethodName}
          onAdd={addPaymentMethod}
          items={paymentMethods.map(p => ({ id: p.method_id, name: p.name, color: p.color || '#3B82F6' }))}
          onUpdate={updatePaymentMethod}
          onDelete={deletePaymentMethod}
          defaultColor="#3B82F6"
          testIdPrefix="payment-method"
        />

        </div>
      </main>
    </div>
  );
};
